import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import AdminShell from "../components/AdminShell";
import { Bell, AlertTriangle, CheckCheck, RefreshCw, ArrowRight } from "lucide-react";

function timeAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    const diff = Math.floor((Date.now() - t) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  } catch { return ""; }
}

export default function AdminAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/alerts");
      setAlerts(data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await api.post(`/admin/alerts/${id}/read`);
    setAlerts(prev => prev.map(a => a.alert_id === id ? { ...a, read: true } : a));
  };

  const markAll = async () => {
    await api.post(`/admin/alerts/read-all`);
    setAlerts(prev => prev.map(a => ({ ...a, read: true })));
  };

  const runScan = async () => {
    setScanning(true); setScanResult("");
    try {
      const { data } = await api.post(`/admin/alerts/scan`);
      setScanResult(data.fired === 0 ? "No new nudges fired." : `Fired ${data.fired} nudge${data.fired === 1 ? "" : "s"}.`);
      await load();
    } catch (e) {
      setScanResult(e.response?.data?.detail || "Scan failed");
    } finally { setScanning(false); }
  };

  const unreadCount = alerts.filter(a => !a.read).length;

  return (
    <AdminShell>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#F59E0B]"/>
            <h1 className="text-3xl font-bold">Onboarding nudges</h1>
          </div>
          <p className="mt-1 text-sm text-neutral-400">
            Alerts when a founder claimed a business but hasn't engaged within 48 hours.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={runScan} disabled={scanning} data-testid="admin-alerts-scan"
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm font-semibold text-neutral-200 hover:bg-neutral-800 disabled:opacity-50">
            <RefreshCw className={"h-4 w-4 " + (scanning ? "animate-spin" : "")}/>
            {scanning ? "Scanning…" : "Run scan now"}
          </button>
          {unreadCount > 0 && (
            <button onClick={markAll} data-testid="admin-alerts-readall"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B] px-4 py-1.5 text-sm font-bold text-neutral-950 hover:bg-[#EAB308]">
              <CheckCheck className="h-4 w-4"/> Mark all read
            </button>
          )}
        </div>
      </div>

      {scanResult && (
        <div className="mt-4 rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-2 text-sm text-neutral-300" data-testid="admin-alerts-scan-result">
          {scanResult}
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900" data-testid="admin-alerts-list">
        {loading && <div className="p-10 text-center text-sm text-neutral-500">Loading…</div>}
        {!loading && alerts.length === 0 && (
          <div className="p-10 text-center">
            <Bell className="mx-auto h-8 w-8 text-neutral-700"/>
            <p className="mt-3 text-sm text-neutral-500">No nudges yet. Once founders claim businesses and stay quiet for 48 hours, they'll show up here.</p>
          </div>
        )}
        <ul className="divide-y divide-neutral-800">
          {alerts.map(a => (
            <li key={a.alert_id} data-testid={`admin-alert-${a.alert_id}`}
                className={"flex items-start gap-3 p-4 " + (a.read ? "opacity-60" : "")}>
              <div className={"mt-0.5 rounded-lg p-2 " + (a.read ? "bg-neutral-800" : "bg-amber-500/15")}>
                <AlertTriangle className={"h-4 w-4 " + (a.read ? "text-neutral-500" : "text-amber-400")}/>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-neutral-100">{a.company_name || "(Untitled)"}</p>
                  {!a.read && (
                    <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-red-400">New</span>
                  )}
                  <span className="ml-auto text-[11px] text-neutral-500">{timeAgo(a.created_at)}</span>
                </div>
                <p className="mt-1 text-sm text-neutral-300">{a.message}</p>
                <div className="mt-2 flex items-center gap-3">
                  <Link to={`/admin/businesses/${a.company_id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#F59E0B] hover:underline"
                    data-testid={`admin-alert-link-${a.alert_id}`}>
                    Open business <ArrowRight className="h-3 w-3"/>
                  </Link>
                  {!a.read && (
                    <button onClick={() => markRead(a.alert_id)}
                      data-testid={`admin-alert-read-${a.alert_id}`}
                      className="text-xs text-neutral-400 hover:text-neutral-200">
                      Mark read
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </AdminShell>
  );
}
